import { useState } from "react";
import "./Tidy.css";
import { Button } from "./Button";
import { Chip, StatusPill } from "./Status";
import type { StatusKey } from "./Status";
import { spanSince } from "./IssueCard";
import { KIND, PROPOSAL_STATUS, isKnownKind, skipText } from "../lib/tidy";
import type { Proposal, ProposalStatus, Skip, TidyState } from "../lib/tidy";
import { tx } from "../i18n";

/* ══ 이슈 정리의 부품 — 채택 시안 안 A(Figma 52-3) · 공통 55-2 ══
   세 단추가 **세 자리**에 있다: 정리 요청은 사이드바 범위 줄 아래(`TidyScope`), 적용은 표 바닥
   선택 막대(`TidyTable`, 사람만), 기각은 줄 끝. 무게가 다른 것을 나란히 두지 않는다 — 축이
   둘이라는 것이 이 기능의 전부다. */

/** 갈래 칩. 모르는 갈래는 **코드 그대로** 그린다 — 서버가 늘린 날 화면이 침묵하지 않게. */
export function KindChip({ kind }: { kind: string }) {
  if (!isKnownKind(kind)) return <Chip tone="muted">{kind}</Chip>;
  const k = KIND[kind];
  return (
    <Chip tone={k.tone}>
      <span aria-hidden="true">{k.glyph}</span> {k.label}
    </Chip>
  );
}

export function ProposalStatusChip({ status }: { status: ProposalStatus }) {
  const s = PROPOSAL_STATUS[status];
  return (
    <Chip tone={s.tone}>
      <span aria-hidden="true">{s.glyph}</span> {s.label}
    </Chip>
  );
}

/** 표가 그리는 이슈 쪽 사실 — `GET /tasks` 의 한 줄에서 이것만 쓴다. */
export type IssueLite = { id: string; title: string; status: StatusKey; projectId?: string | null };

/** 범위 줄 — **세 조각이고 셋 다 뜻이 다르다**: 어디를(이 설치) · 얼마나 큰 판인가(열린 이슈
 *  수) · 언제 훑었나(마지막 정리). 뒤의 둘이 빠지면 「방금 것인가 지난주 것인가」를 화면에서
 *  알 수 없어 사람이 확인하려고 세션부터 다시 돌린다(b3x-pr 리뷰 반려 2026-09-03).
 *
 *  정리 범위는 이 설치 전체다 — 프로젝트 축(0024)이 생겼어도 정리는 아직 프로젝트로 안 접는다(`tidy.md`). */
export function TidyScope({
  state,
  openCount,
  busy,
  onRequest,
  now = Date.now(),
}: {
  state: TidyState | null;
  openCount: number | null;
  busy?: boolean;
  onRequest: () => void;
  now?: number;
}) {
  const a = state?.anchor ?? null;
  const last = state
    ? state.lastSweepAt
      ? tx("issue.ago", { when: spanSince(state.lastSweepAt, now) })
      : tx("tidy.scope.never")
    : tx("chat.unknown");
  const phase = a?.running ? `◐ ${tx("issue.working")}` : a?.requestedAt ? `◔ ${tx("tidy.scope.requested")}` : null;
  return (
    <div className="b-card tscope">
      <span className="tscope__label">{tx("tidy.scope.label")}</span>
      <span className="tscope__where">{tx("tidy.scope.where")}</span>
      <span className="tscope__fact">{tx("tidy.scope.open", { n: openCount === null ? tx("chat.unknown") : tx("alarm.page.count", { n: openCount }) })}</span>
      <span className="tscope__fact">{tx("tidy.scope.last", { when: last })}</span>
      {phase ? (
        <span className="tscope__phase" role="status">
          {phase}
          {a ? <code className="tscope__anchor">{a.id}</code> : null}
        </span>
      ) : null}
      <Button size="md" onClick={onRequest} loading={busy} disabled={!!phase || state === null} className="tscope__btn">
        ↻ {tx("tidy.request")}
      </Button>
      <span className="tscope__note">{tx("tidy.scope.note")}</span>
    </div>
  );
}


/** 적용 결과. **늘 두 숫자를 함께 말한다** — 건너뜀이 0 이어도 적는다. 건너뛴 제안이 목록에
 *  그대로 남는다는 사실까지 적어야 사람이 「사라졌다」로 읽지 않는다. */
export function TidyResult({
  applied,
  skipped,
}: {
  applied: number;
  skipped: { proposalId: string; taskId: string; skip: Skip }[];
}) {
  return (
    <div className="tresult" role="status">
      <div className="tresult__head">
        <Chip tone="success">● {tx("tidy.result.applied", { n: applied })}</Chip>
        <Chip tone={skipped.length ? "warning" : "muted"}>↷ {tx("tidy.result.skipped", { n: skipped.length })}</Chip>
        <span className="tresult__note">{tx("tidy.result.note")}</span>
      </div>
      {skipped.map((s) => (
        <div className="tresult__skip" key={s.proposalId}>
          <span aria-hidden="true" className="tresult__glyph">↷</span>
          <code className="tresult__id">{s.taskId}</code>
          <span className="tresult__text">{skipText(s.skip)}</span>
          <code className="tresult__code">{s.skip.code}</code>
        </div>
      ))}
    </div>
  );
}

/** 근거 — 길면 세 줄로 접고 「근거 펼치기」를 단다(원본 pr.b3x.app 은 긴 근거를 통째로 펼쳐 표가 길어졌다).
 *  짧은 근거엔 단추가 없다 — 펼칠 것이 없는 단추를 두지 않는다. */
export const REASON_FOLD = 180;
export function TidyReason({ text, initialOpen = false }: { text: string; initialOpen?: boolean }) {
  const [open, setOpen] = useState(initialOpen);
  const long = text.length > REASON_FOLD || text.split("\n").length > 3;
  return (
    <>
      <span className={`ttable__reason${long && !open ? " is-folded" : ""}`}>{text}</span>
      {long ? (
        <button type="button" className="ttable__more" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {tx(open ? "tidy.reasonLess" : "tidy.reasonMore")}
        </button>
      ) : null}
    </>
  );
}

export type TidyRow = { p: Proposal; task?: IssueLite; ref?: IssueLite };

/** 제안 표. 줄 한 줄이 **무엇을(이슈) · 어떻게(갈래) · 왜(근거)** 다.
 *
 *  열린 제안만 고를 수 있다 — 적용됨·기각됨은 지난 기록이라 칸이 없다. 적용 단추는 표 바닥의
 *  선택 막대 **하나**다(사람만). 좁은 화면(≤960px)에서는 줄이 카드로 접히고 막대는 그 목록의
 *  바닥에 붙는다 — `position: fixed` 를 쓰지 않는다. */
export function TidyTable({
  rows,
  selected,
  busy,
  onToggle,
  onToggleAll,
  onDismiss,
  onApply,
  onClear,
  now = Date.now(),
  initialConfirm = false,
}: {
  initialConfirm?: boolean;
  rows: TidyRow[];
  selected: Set<string>;
  busy?: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onDismiss: (id: string) => void;
  onApply: () => void;
  onClear: () => void;
  now?: number;
}) {
  // 적용은 이슈를 실제로 옮긴다 — 막대 안에서 한 번 더 묻는다(보드 선택 막대의 삭제와 같은 모양).
  const [confirm, setConfirm] = useState(initialConfirm);
  const openRows = rows.filter((r) => r.p.status === "open");
  const allOn = openRows.length > 0 && openRows.every((r) => selected.has(r.p.id));
  const picked = rows.filter((r) => selected.has(r.p.id));
  const byKind = new Map<string, number>();
  for (const r of picked) byKind.set(r.p.kind, (byKind.get(r.p.kind) ?? 0) + 1);
  return (
    <div className="ttable">
      <div className="b-table-wrap">
      <table className="b-table">
        <thead>
          <tr>
            <th className="ttable__cb">
              <label className="b-check">
                <input
                  type="checkbox"
                  aria-label={tx("tidy.table.pickAll")}
                  checked={allOn}
                  disabled={!openRows.length || busy}
                  onChange={onToggleAll}
                />
              </label>
            </th>
            <th>{tx("tidy.table.issue")}</th>
            <th>{tx("tidy.table.kind")}</th>
            <th>{tx("tidy.table.why")}</th>
            <th>{tx("tidy.table.when")}</th>
            <th aria-label={tx("tidy.dismiss")} />
          </tr>
        </thead>
        <tbody>
          {rows.map(({ p, task, ref }) => {
            const open = p.status === "open";
            return (
              <tr key={p.id} className={selected.has(p.id) ? "is-selected" : undefined} aria-selected={selected.has(p.id) || undefined}>
                <td className="ttable__cb">
                  {open ? (
                    <label className="b-check">
                      <input
                        type="checkbox"
                        aria-label={tx("tidy.table.pick", { title: task?.title ?? p.taskId })}
                        checked={selected.has(p.id)}
                        disabled={busy}
                        onChange={() => onToggle(p.id)}
                      />
                    </label>
                  ) : null}
                </td>
                <td className="ttable__issue" data-label={tx("chat.issue")}>
                  {/* 이슈가 목록에 없으면(지워졌거나 못 읽음) 「모름」이라고 쓴다 — 제목을 지어내지 않는다. */}
                  <span className="ttable__title">{task?.title ?? tx("tidy.table.noTitle")}</span>
                  <span className="ttable__meta">
                    <code>{p.taskId}</code>
                    {task ? <StatusPill status={task.status} /> : null}
                  </span>
                </td>
                <td data-label={tx("alarm.table.kind")}>
                  <KindChip kind={p.kind} />
                  {!open ? <ProposalStatusChip status={p.status} /> : null}
                </td>
                <td className="ttable__why" data-label={tx("tidy.table.reason")}>
                  <TidyReason text={p.reason} />
                  {p.refTaskId ? (
                    <span className="ttable__ref">
                      {tx(p.kind === "duplicate" ? "tidy.table.keep" : "tidy.table.designRef")} → <code>{p.refTaskId}</code>
                      {ref ? ` 「${ref.title}」` : ` ${tx("tidy.table.notListed")}`}
                    </span>
                  ) : null}
                </td>
                <td className="ttable__when" data-label={tx("tidy.table.when")}>
                  {tx("issue.ago", { when: spanSince(p.createdAt, now) })}
                </td>
                <td className="ttable__end">
                  {open ? (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => onDismiss(p.id)} title={tx("tidy.dismissTip")}>
                      ✕ {tx("tidy.dismiss")}
                    </Button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {/* 선택 막대 — 표 **안쪽 바닥**에 붙는다. 고른 것이 없으면 적용 단추는 비활성이다. */}
      <div className="tbar">
        <span className="tbar__count">{picked.length ? tx("tidy.bar.picked", { n: picked.length }) : tx("tidy.bar.none")}</span>
        {[...byKind].map(([k, n]) => (
          <span key={k} className="tbar__kind">
            <KindChip kind={k} /> {n}
          </span>
        ))}
        <span className="tbar__grow" />
        {confirm && picked.length ? (
          <>
            <span className="tbar__ask" role="alert">
              <span className="tbar__ask-glyph" aria-hidden="true">▲</span> {tx("tidy.bar.confirm", { n: picked.length })}
            </span>
            <Button size="md" variant="ghost" onClick={() => setConfirm(false)} disabled={busy}>
              {tx("tidy.bar.back")}
            </Button>
            <Button
              size="md"
              variant="primary"
              loading={busy}
              onClick={() => {
                setConfirm(false);
                onApply();
              }}
            >
              {tx("tidy.bar.go")}
            </Button>
          </>
        ) : (
          <>
            <Button size="md" variant="ghost" onClick={onClear} disabled={!picked.length || busy}>
              {tx("alarm.table.clear")}
            </Button>
            <Button size="md" variant="primary" onClick={() => setConfirm(true)} disabled={!picked.length} loading={busy}>
              {tx("tidy.bar.apply")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** 표 밑 한 줄 — 갈래마다 적용이 무엇을 하는지. 안 A 가 잃는 것(효과가 표 밖에 있다)을 메운다. */
export function TidyLegend() {
  return (
    <p className="tlegend">
      {tx("tidy.legend")}{" "}
      {Object.entries(KIND).map(([k, v], i) => (
        <span key={k}>
          {i ? " · " : ""}
          {v.glyph} {v.label} → {v.applies}
        </span>
      ))}
      .
    </p>
  );
}
